import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useCanal } from '../lib/tiempoReal'
import { clasesDeColor } from '../lib/estados'
import { puede } from '../lib/permisos'
import { CambiarEstado } from '../componentes/CambiarEstado'
import type { Catalogos, FichaEnLista, Usuario } from '../lib/tipos'

const COLUMNAS = [
  'Código',
  'Clase',
  'Cliente',
  'Fecha de baja',
  'Puntos',
  'CUPS',
  'Compañía',
  'Comercial',
]

const fecha = (valor: string | null): string =>
  valor === null ? '—' : new Date(valor.replace(' ', 'T')).toLocaleDateString('es-ES')

/**
 * Bajas, en su propio apartado.
 *
 * Era la quinta duda de octubre: «Bajas» aparecía como apartado del menú y a la
 * vez BAJA era un estado dentro de Contratos, y lo tenía montado como la misma
 * pantalla con el estado filtrado. José lo cerró el 15 de octubre: «es un estado
 * de contrato, pero cuando entra un contrato en bajas aparecería en el apartado
 * de bajas, no en contratos». Así que es una pantalla aparte y con sus columnas:
 * lo que importa aquí es de qué clase es la baja, cuándo fue y a quién le quita
 * puntos, no en qué fase estaba la llamada.
 */
export function Bajas({ usuario, ir }: { usuario: Usuario; ir: (destino: string) => void }) {
  const clienteQuery = useQueryClient()
  const [busqueda, setBusqueda] = useState('')
  const [clase, setClase] = useState('')
  const [aviso, setAviso] = useState<string | null>(null)

  const lista = useQuery({
    queryKey: ['bajas', clase, busqueda],
    queryFn: () =>
      api<{ fichas: FichaEnLista[] }>(
        `/api/fichas?estado=BAJA&subestado=${encodeURIComponent(clase)}` +
          `&busqueda=${encodeURIComponent(busqueda)}`,
      ),
  })

  const catalogos = useQuery({
    queryKey: ['catalogos'],
    queryFn: () => api<Catalogos>('/api/catalogos'),
  })

  useCanal('sala', () => void clienteQuery.invalidateQueries({ queryKey: ['bajas'] }))

  const fichas = lista.data?.fichas ?? []
  const catalogo = catalogos.data?.estados ?? []
  const clases = catalogo.find((item) => item.codigo === 'BAJA')?.subestados ?? []
  const cambiaEstados = puede(usuario, 'contratos.cambiar_estado')
  const exporta = puede(usuario, 'contratos.exportar')

  const descomisionables = fichas.filter((ficha) => ficha.baja_tipo === 'descomisionable').length
  const puntosPerdidos = fichas.reduce((suma, ficha) => suma + (ficha.puntos_computados ?? 0), 0)

  const exportar = () => {
    const filas = fichas.map((ficha) => [
      ficha.codigo,
      ficha.subestado_etiqueta ?? ficha.estado_etiqueta,
      ficha.cliente ?? '',
      ficha.baja_el ?? '',
      ficha.puntos_computados ?? '',
      ficha.cups ?? '',
      ficha.compania ?? '',
      ficha.comercial,
    ])

    const csv = [COLUMNAS, ...filas]
      .map((fila) => fila.map((celda) => `"${String(celda).replace(/"/g, '""')}"`).join(';'))
      .join('\r\n')

    const enlace = document.createElement('a')
    enlace.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }))
    enlace.download = `bajas-${new Date().toISOString().slice(0, 10)}.csv`
    enlace.click()
    URL.revokeObjectURL(enlace.href)
  }

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-lg font-bold">Bajas</h1>
        <p className="text-xs text-slate-500">
          La clase de baja la decide Codoh mirando si el mes de la activación ya se liquidó.
          Backoffice solo dice «baja».
        </p>
      </header>

      <div className="mb-4 grid gap-2 sm:grid-cols-3">
        <Dato etiqueta="Bajas" valor={fichas.length.toLocaleString('es-ES')} />
        <Dato etiqueta="Descomisionables" valor={descomisionables.toLocaleString('es-ES')} rojo />
        <Dato etiqueta="Puntos afectados" valor={puntosPerdidos.toLocaleString('es-ES')} rojo />
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Nombre del cliente, código, teléfono o CUPS"
          className="min-w-52 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-marca-500 dark:border-slate-700 dark:bg-slate-950"
        />
        {exporta && fichas.length > 0 && (
          <button
            onClick={exportar}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium transition hover:border-marca-500 dark:border-slate-700"
          >
            Exportar a Excel
          </button>
        )}
      </div>

      {clases.length > 0 && (
        <nav className="mb-3 flex flex-wrap gap-1">
          <Pestaña activa={clase === ''} alPulsar={() => setClase('')}>
            Todas
          </Pestaña>
          {clases.map((sub) => (
            <Pestaña key={sub.id} activa={clase === sub.codigo} alPulsar={() => setClase(sub.codigo)}>
              {sub.etiqueta}
            </Pestaña>
          ))}
        </nav>
      )}

      {aviso && (
        <p className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {aviso}
        </p>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        {lista.isLoading && <p className="px-4 py-6 text-sm text-slate-400">Cargando…</p>}
        {fichas.length === 0 && !lista.isLoading && (
          <p className="px-4 py-6 text-sm text-slate-400">Ninguna baja por aquí. Mejor así.</p>
        )}
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {fichas.map((ficha) => (
            // Igual que en Contratos: la fila entera abre el contrato, y el
            // cambio de estado va pegado al estado.
            <li
              key={ficha.uuid}
              onClick={() => ir(`/fichas/${ficha.uuid}`)}
              className="flex cursor-pointer flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm transition hover:bg-slate-50 dark:hover:bg-slate-800/60"
            >
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  ir(`/fichas/${ficha.uuid}`)
                }}
                className="font-mono font-semibold hover:underline"
              >
                {ficha.codigo}
              </button>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${clasesDeColor(
                  ficha.baja_tipo === 'descomisionable' ? 'rojo' : 'naranja',
                )}`}
              >
                {ficha.subestado_etiqueta ?? 'Baja'}
              </span>
              {cambiaEstados && (
                <span onClick={(e) => e.stopPropagation()}>
                  <CambiarEstado
                    uuid={ficha.uuid}
                    acciones={ficha.acciones}
                    catalogo={catalogo}
                    alFallar={setAviso}
                  />
                </span>
              )}
              {ficha.cliente && <span className="font-medium">{ficha.cliente}</span>}
              <span className="text-xs text-slate-500">de baja el {fecha(ficha.baja_el)}</span>
              {ficha.puntos_computados !== null && ficha.puntos_computados > 0 && (
                <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">
                  −{ficha.puntos_computados} puntos
                </span>
              )}
              {ficha.cups && <span className="font-mono text-xs text-slate-400">{ficha.cups}</span>}
              {ficha.compania && <span className="text-xs text-slate-500">{ficha.compania}</span>}
              <span className="text-xs text-slate-400">{ficha.comercial}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

function Dato({ etiqueta, valor, rojo }: { etiqueta: string; valor: string; rojo?: boolean }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
      <p className="text-xs text-slate-500">{etiqueta}</p>
      <p
        className={`text-2xl font-bold tabular-nums ${
          rojo ? 'text-red-700 dark:text-red-400' : ''
        }`}
      >
        {valor}
      </p>
    </div>
  )
}

function Pestaña({
  activa,
  alPulsar,
  children,
}: {
  activa: boolean
  alPulsar: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={alPulsar}
      className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
        activa
          ? 'bg-marca-700 text-white'
          : 'border border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300'
      }`}
    >
      {children}
    </button>
  )
}
